import { Box, Chip, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../../hooks/uiHook";
import { useRestaurant } from "../../hooks/RestaurantContext";
import { formatDateTime } from "../../global/functions/dateFormat";
import { formatStatusText } from "../../models/status.model";
import { DashboardCard } from "../../components/dashboardCard";

const STATUS_CHIP = {
  enCours: { bgcolor: "rgba(66,165,245,0.16)", color: "#64b5f6" },
  terminee: { bgcolor: "rgba(73,190,37,0.16)", color: "#49BE25" },
  annulee: { bgcolor: "rgba(255,172,71,0.16)", color: "#ffac47" },
  echouee: { bgcolor: "rgba(233,49,60,0.16)", color: "#E9313C" },
  remboursee: { bgcolor: "rgba(236,29,35,0.16)", color: "#ec1d23" },
  enRetard: { bgcolor: "rgba(240,68,74,0.16)", color: "#f0444a" },
  enAttente: { bgcolor: "rgba(255,172,71,0.16)", color: "#ffac47" },
};

const SecondRow = ({ recents }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useRestaurant();
  const { isSmallScreen } = useResponsive();
  const orders = Array.isArray(recents) ? recents : [];

  return (
    <DashboardCard
      sx={{
        gridColumn: isSmallScreen ? "1 / -1" : "span 4",
        gridRow: "span 2",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        maxHeight: "100%",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        pt={2}
        pb={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptLongIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            {t("dashboard.recentOrders")}
          </Typography>
        </Box>
        <Button size="small" color="primary" onClick={() => navigate("/history")}>
          {t("dashboard.viewAll")}
        </Button>
      </Box>
      {orders.length === 0 ? (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={2}
          pb={2}
        >
          <Typography color="text.secondary">{t("dashboard.noRecent")}</Typography>
        </Box>
      ) : (
        orders.map((recent) => {
          const products = Array.isArray(recent.product) ? recent.product : [];
          const addon = products.reduce(
            (sum, prod) => sum + (prod.addons?.length || 0),
            0
          );
          const plat = products.length;
          return (
            <Box
              key={recent._id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              px={2}
              py={1.25}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box minWidth={0}>
                <Typography variant="h5" fontWeight={700} noWrap>
                  #{recent.commandNumber || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {t("dashboard.plates", { count: plat })} ·{" "}
                  {t("dashboard.addons", { count: addon })}
                </Typography>
                <Box display="flex" gap={0.75} mt={0.75} flexWrap="wrap">
                  {recent.pack?.label && (
                    <Chip size="small" label={recent.pack.label} variant="outlined" />
                  )}
                  {recent.status && (
                    <Chip
                      size="small"
                      label={t(`status.${recent.status}`, {
                        defaultValue: formatStatusText(recent.status),
                      })}
                      sx={{
                        fontWeight: 700,
                        ...(STATUS_CHIP[recent.status] || {}),
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Box textAlign="end" flexShrink={0}>
                <Typography variant="h5" fontWeight={800} color="success.main">
                  {recent.total} {currency}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(recent.boughtAt)}
                </Typography>
              </Box>
            </Box>
          );
        })
      )}
    </DashboardCard>
  );
};

export default SecondRow;
